import React, { useEffect, useMemo } from 'react';
import Table from "src/componets/Table";
import { useAppDispatch, useAppSelector } from "src/store/hooks";
import { ColumnsType } from "antd/es/table";
import { UserNumberSubscriptionInterface } from "src/store/callrecorder/userNumberSubscriptionsSlice";
import { fetchUserNumberSubscriptions } from "src/actions/callrecorder/userNumberSubscriptionsActions";
import { formattedDate } from "src/helpers/DateHelpers";
import Pagination from "src/componets/Pagination";
import OptionalLink from "src/componets/OptionalLink";

import { Tag } from "antd";

interface ListTableInterface {
  filterParams: object;
  limit?: number;
}

const _ListTable = ({ filterParams, limit = 25 }: ListTableInterface) => {
  const dispatch = useAppDispatch();
  const ids = useAppSelector(state => state.callrecorder.userNumberSubscriptions.ids);
  const entities = useAppSelector(state => state.callrecorder.userNumberSubscriptions.entities);
  const count = useAppSelector(state => state.callrecorder.userNumberSubscriptions.count);
  const data = ids ? ids.map(id => entities[id]) : [];

  const params = useMemo(() => ({
    limit,
    ...filterParams
  }), [limit, filterParams]);

  useEffect(() => {
    dispatch(fetchUserNumberSubscriptions(params));
  }, [params, dispatch]);

  const columns: ColumnsType<UserNumberSubscriptionInterface> = [
    {
      key: 'id',
      title: 'ID',
      render: (_, { id }) => <small>{`#${id}`}</small>,
      width: 60
    },

    {
      key: 'plan_name',
      title: 'Plan',
      render: (_, { plan_name }) => {
        if (!plan_name) return '—';
        return <small>{plan_name}</small>;
      },
      width: 100
    },

    {
      key: 'status',
      title: 'Status',
      render: (_, { status }) => {
        const color = status === 'active' ? 'success'
          : status === 'pending' ? 'processing'
          : status === 'cancelled' ? 'warning'
          : status === 'expired' ? 'error'
          : 'default';
        return <Tag color={color}>{status}</Tag>;
      },
      width: 90
    },

    {
      key: 'auto_renew',
      title: 'Auto Renew',
      render: (_, { auto_renew }) => auto_renew ? <Tag color="success">yes</Tag> : <Tag color="default">no</Tag>,
      width: 70
    },

    {
      key: 'number',
      title: 'User Number',
      render: (_, { user_number_id, number }) => <OptionalLink link={`/callrecorder/user_numbers/${user_number_id}`}><span>{`+${number}`}</span></OptionalLink>,
      width: 80
    },

    {
      key: 'incoming_sms_count',
      title: 'SMS In',
      render: (_, { incoming_sms_count }) => <small>{incoming_sms_count}</small>,
      width: 55
    },

    {
      key: 'total_incoming_sms_limit',
      title: 'SMS Limit',
      render: (_, { total_incoming_sms_limit }) => <small>{total_incoming_sms_limit}</small>,
      width: 60
    },

    {
      key: 'extra_incoming_sms_count',
      title: 'Extra SMS',
      render: (_, { extra_incoming_sms_count }) => <small>{extra_incoming_sms_count}</small>,
      width: 60
    },

    {
      key: 'monthly_fee',
      title: 'Monthly Fee',
      render: (_, { monthly_fee }) => <small>{monthly_fee || '—'}</small>,
      width: 80
    },

    {
      key: 'days_remaining',
      title: 'Days Left',
      render: (_, { days_remaining }) => <small>{days_remaining ?? '—'}</small>,
      width: 65
    },

    {
      key: 'started_at',
      title: 'Started',
      render: (_, { started_at }) => <small>{formattedDate(started_at).dateTime}</small>,
      width: 125
    },

    {
      key: 'current_period_end',
      title: 'Period End',
      render: (_, { current_period_end }) => <small>{formattedDate(current_period_end).dateTime}</small>,
      width: 125
    },
  ];

  return (
    <div>
      <Table
        rowKey='id'
        columns={columns}
        dataSource={data}
        search_keys={['number']}
        sort_keys={['id', 'current_period_end', 'started_at']}
      />

      <Pagination className='v-mt-10' total={count} size={limit} />
    </div>
  )
}

export default _ListTable;
